import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { machinesApi } from '../services/machinesApi';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

interface Options {
  /** Restrict the machine list to a single type (e.g. 'composter' or 'lifter'). */
  machineType?: 'composter' | 'lifter';
  defaultDays?: number;
}

// Shared hook: machines query + persistent (per-mount) selection + date range.
export const useMachineFilters = ({ machineType, defaultDays = 29 }: Options = {}) => {
  const [machineId, setMachineId] = useState<string | null>(null);
  const [from, setFrom] = useState(daysAgoIso(defaultDays));
  const [to, setTo] = useState(todayIso());

  const machinesQuery = useQuery({
    queryKey: ['machines'],
    queryFn: async () => (await machinesApi.list()).data,
  });

  const filteredMachines = useMemo(() => {
    if (!machinesQuery.data) return undefined;
    if (!machineType) return machinesQuery.data;
    return machinesQuery.data.filter((m) => m.machineType === machineType);
  }, [machinesQuery.data, machineType]);

  const selectedMachineId = useMemo(() => {
    if (machineId) return machineId;
    return filteredMachines?.[0]?.id ?? null;
  }, [machineId, filteredMachines]);

  return {
    machines: filteredMachines,
    machinesLoading: machinesQuery.isLoading,
    selectedMachineId,
    setMachineId,
    from,
    to,
    setFrom,
    setTo,
  };
};
